import React, { useState } from 'react';
import PageSEO from '../components/PageSEO';
import WebContactForm from '../components/WebContactForm';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function TvInspektionPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const tvImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/tv-inspektion-kloakledning.webp",
      alt: "TV-inspektion inde i kloakledning vist på inspektionsskærm",
      caption: "TV-inspektion inde i kloakledning vist på inspektionsskærm"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kloakspuling-kloakspuler.webp",
      alt: "Professionelt udstyr til kloakspuling",
      caption: "Professionelt udstyr til kloakspuling"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/tv-inspektion-udstyr.webp",
      alt: "TV-inspektionsudstyr ved åben kloakbrønd",
      caption: "TV-inspektionsudstyr ved åben kloakbrønd"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/tv-inspektionsbil.webp",
      alt: "TV-inspektionsbil ved kloakrist",
      caption: "TV-inspektionsbil ved kloakrist"
    }
  ];

  const relevantCases = [
    'Gentagne tilstopninger',
    'Mistanke om brud eller sætningsskader',
    'Forskudte samlinger',
    'Rødder eller aflejringer i ledningen',
    'Mistanke om rotteskader',
    'Problemer med fugt eller oversvømmelse',
    'Undersøgelse i forbindelse med hushandel',
    'Kontrol af udført kloakarbejde',
    'Lokalisering af skjulte ledninger og brønde'
  ];

  const kimContact = SITE_INFO.contacts.find((c) => c.name.includes('Kim')) || {
    name: 'Kim Hougaard Jensen',
    role: 'TV-inspektion',
    phone: '23 65 25 77',
    email: 'Kim@kts-entreprise.dk'
  };

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="TV-inspektion og kloakspuling | Nordsjælland | KTS-Entreprise A/S"
        description="KTS-Entreprise udfører TV-inspektion og kloakspuling ved problemer med kloakledninger. Vi kan lokalisere fejl, rense ledninger og udarbejde rapport med billeder og video efter aftale."
        ogTitle="TV-inspektion og kloakspuling | KTS-Entreprise A/S"
        ogDescription="Kameraanalyse og højtryksspuling af kloakledninger i Nordsjælland med digital dokumentation til f.eks. forsikringssager."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="tv-inspektion"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Diagnostik &amp; Rensning</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            TV-inspektion og kloakspuling
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            KTS-Entreprise udfører TV-inspektion og kloakspuling ved problemer med kloakledninger. Vi kan lokalisere fejl, rense ledninger og udarbejde rapport med billeder og video efter aftale.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Hvornår kan en TV-inspektion være relevant? */}
        <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs mb-12">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Hvornår kan en TV-inspektion være relevant?
            </h2>
            <p className="text-stone-600 text-xs sm:text-sm mt-1">
              En kameraanalyse giver fuldt overblik over kloakkens indvendige tilstand.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {relevantCases.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-xs"
              >
                <i className="fa-solid fa-check text-[#0072c0] text-sm shrink-0" aria-hidden="true"></i>
                <span className="font-heading font-bold uppercase text-xs sm:text-sm text-stone-800">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sådan foregår en TV-inspektion & Kloakspuling */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          
          <div className="lg:col-span-8 space-y-6">
            {/* Sådan foregår det */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-4">
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
                <h3 className="text-xl sm:text-2xl font-bold uppercase text-stone-900 font-heading">
                  Sådan foregår en TV-inspektion
                </h3>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Et kamera føres gennem kloakledningen, mens relevante observationer registreres. Det kan blandt andet være grenrør, samlinger, aflejringer, rødder, brud og andre skader.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Efter aftale udarbejdes en rapport med billeder og video. Rapporten kan bruges som grundlag for vurdering af eventuelle reparationer eller i dialogen med et forsikringsselskab.
              </p>

              {/* Billede: TV-inspektion inde i kloakledning */}
              <div className="overflow-hidden border border-stone-200 rounded-xs bg-stone-900 my-4">
                <ClickableWorkImage
                  src={tvImages[0].src}
                  alt={tvImages[0].alt}
                  className="w-full h-auto object-cover max-h-[420px]"
                  onClick={() => setLightboxState({ isOpen: true, index: 0 })}
                />
                <div className="p-3 bg-stone-900 text-stone-300 text-xs flex items-center justify-between border-t border-stone-800">
                  <span>Optagelse og skadeslokalisering direkte på inspektionsskærm</span>
                  <span className="text-[#0072c0] font-heading font-bold uppercase text-[11px]">IPEK System</span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xs space-y-2 text-xs sm:text-sm text-stone-700">
                <p className="font-semibold text-stone-900">Digital udlevering:</p>
                <p>
                  Billeder, video og den samlede rapport udleveres digitalt som rapport og dokumentation, som blandt andet kan anvendes i forbindelse med forsikringssager.
                </p>
                <p className="pt-2 text-stone-600 border-t border-slate-200">
                  <strong>Adgangskrav:</strong> Kloakbrønde og øvrige adgangspunkter skal være tilgængelige, inden undersøgelsen udføres. Skjulte brønde under terrasser, belægninger eller bygninger kan kræve adgang, før arbejdet kan gennemføres.
                </p>
              </div>
            </div>

            {/* Kloakspuling */}
            <div className="bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs space-y-4">
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
                <h3 className="text-xl sm:text-2xl font-bold uppercase text-stone-900 font-heading">
                  Kloakspuling
                </h3>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Kloakspuling anvendes til rensning af tilstoppede eller tilsmudsede kloakledninger. Spulingen kan blandt andet fjerne sand, slam, fedtaflejringer og andre ophobninger i rørene.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                TV-inspektion og spuling kan kombineres, så ledningen først renses og derefter undersøges.
              </p>

              <div className="overflow-hidden border border-stone-200 rounded-xs bg-white my-4">
                <ClickableWorkImage
                  src={tvImages[1].src}
                  alt={tvImages[1].alt}
                  className="w-full h-auto object-cover max-h-[360px]"
                  onClick={() => setLightboxState({ isOpen: true, index: 1 })}
                />
              </div>

              <div className="bg-stone-50 border border-stone-200 p-4 rounded-xs mt-3">
                <h4 className="font-heading font-bold uppercase text-xs tracking-wider text-stone-900 mb-2">
                  Udstyrsinformation:
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm text-stone-800">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-gauge text-[#0072c0]"></i>
                    <span>Op til 150 bar</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-faucet text-[#0072c0]"></i>
                    <span>Op til 60 liter/min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-droplet text-[#0072c0]"></i>
                    <span>360 liter vandtank</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Visuals & Contact (No Portraits) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Primært billede: TV-inspektionsudstyr ved åben kloakbrønd */}
            <div className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs">
              <div className="overflow-hidden rounded-xs">
                <ClickableWorkImage
                  src={tvImages[2].src}
                  alt={tvImages[2].alt}
                  className="w-full h-auto object-cover"
                  onClick={() => setLightboxState({ isOpen: true, index: 2 })}
                />
              </div>
              <div className="px-2 pt-2.5 pb-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-stone-500 block">
                  Professionelt udstyr
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase">
                  TV-inspektion ved åben kloakbrønd
                </p>
              </div>
            </div>

            {/* Kontaktboks - Uden portrætfoto */}
            <div className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs">
              <div className="border-l-3 border-[#0072c0] pl-3 mb-4">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                  Kloakspuling &amp; TV-inspektion
                </span>
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase">
                  Direkte kontakt
                </h3>
              </div>

              <div className="mb-4">
                <h4 className="font-heading font-bold text-stone-900 text-base">
                  {kimContact.name}
                </h4>
                <p className="text-xs text-stone-600 font-medium mt-0.5">
                  {kimContact.role || 'TV-inspektion & spuling'}
                </p>
              </div>

              <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm">
                <a
                  href={`tel:${kimContact.phone.replace(/\s+/g, '')}`}
                  className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#0072c0]"></i>
                  <span>Telefon: {kimContact.phone}</span>
                </a>
                <a
                  href={`mailto:${kimContact.email}`}
                  className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                  <span>{kimContact.email}</span>
                </a>
              </div>
            </div>

            {/* Understøttende billede: TV-inspektionsbil */}
            <div className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs">
              <div className="overflow-hidden rounded-xs">
                <ClickableWorkImage
                  src={tvImages[3].src}
                  alt={tvImages[3].alt}
                  className="w-full h-auto object-cover"
                  onClick={() => setLightboxState({ isOpen: true, index: 3 })}
                />
              </div>
              <div className="px-2 pt-2.5 pb-1">
                <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-stone-500 block">
                  Servicevogn
                </span>
                <p className="text-xs font-bold text-stone-900 font-heading uppercase">
                  TV-inspektionsbil og udstyr
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* Formular (Afsnit 20) */}
        <div className="mt-16">
          <WebContactForm
            title="Bestil TV-inspektion eller kloakspuling"
            subtitle="Udfyld formularen herunder, så kontakter vi dig hurtigst muligt for at aftale tid."
            submitText="Bestil TV-inspektion eller kloakspuling"
            showAddress={true}
            showTaskTypeSelect={true}
            taskTypeOptions={[
              "Både TV-inspektion og kloakspuling",
              "Kun TV-inspektion",
              "Kun kloakspuling / rensning"
            ]}
            customFields={
              <div className="space-y-4">
                <div>
                  <label htmlFor="wells_accessible" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Er kloakbrøndene synlige og tilgængelige?
                  </label>
                  <select
                    id="wells_accessible"
                    name="wells_accessible"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Ja, alle brønde er synlige og frit tilgængelige">
                      Ja, alle brønde er synlige og frit tilgængelige
                    </option>
                    <option value="Nej, en eller flere brønde er skjult under terrassebrædder eller belægning">
                      Nej, en eller flere brønde er skjult under terrassebrædder eller belægning
                    </option>
                    <option value="Ved ikke – det skal undersøges på adressen">
                      Ved ikke – det skal undersøges på adressen
                    </option>
                  </select>
                </div>

                <div>
                  <label htmlFor="case_context" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Drejer det sig om forsikring eller hushandel?
                  </label>
                  <select
                    id="case_context"
                    name="case_context"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Almindelig tilstopning / service">Almindelig tilstopning / service</option>
                    <option value="Forsikringssag / skadesanmeldelse">Forsikringssag / skadesanmeldelse</option>
                    <option value="Hushandel / køberundersøgelse">Hushandel / køberundersøgelse</option>
                    <option value="Kontrol af udført kloakarbejde">Kontrol af udført kloakarbejde</option>
                  </select>
                </div>
              </div>
            }
            showFileUpload={true}
            fileLabel="Vedhæft gerne billeder, tegninger eller tidligere rapporter (valgfrit)"
          />
        </div>

        {/* Lightbox for TV-inspektion og kloakspuling */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={tvImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="TV-inspektion og kloakspuling"
        />

      </div>
    </div>
  );
}
